import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Plus, Settings2, TrendingUp, TrendingDown, CalendarDays, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import HoursScheduleDialog from "./HoursScheduleDialog";
import HoursBatchEntryDialog from "./HoursBatchEntryDialog";
import OvertimeConfigDialog from "./OvertimeConfigDialog";
import HoursMonthDetail from "./HoursMonthDetail";

const MONTH_LABELS = ["Janv.", "Févr.", "Mars", "Avril", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."];

const formatPeriod = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export const HoursTab = ({ employee, year, onRefresh }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [overtimeOpen, setOvertimeOpen] = useState(false);
  const now = useMemo(() => new Date(), []);
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.getEmployeeHoursSummary(employee.id, { year, month: selectedMonth });
      setData(data);
    } catch {
      toast.error("Erreur chargement des heures");
    } finally {
      setLoading(false);
    }
  }, [employee.id, year, selectedMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const monthly = data?.monthly;
  const yearly = data?.yearly;
  const byMonth = data?.by_month || [];
  const schedule = data?.work_schedule;
  const fiscalPeriod = data?.fiscal_period;
  const overtimeCfg = data?.overtime_config;
  const isAnnual = overtimeCfg?.mode === "annual";

  const monthDelta = monthly?.delta_hours || 0;
  const yearDelta = yearly?.delta_hours || 0;
  const fpDelta = fiscalPeriod?.delta_hours || 0;
  const counterValue = isAnnual ? fpDelta : yearDelta;
  const counterLabel = isAnnual
    ? `Période ${formatPeriod(fiscalPeriod?.start_date)} → ${formatPeriod(fiscalPeriod?.end_date)}`
    : `Compteur ${year}`;

  return (
    <div className="space-y-5">
      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v, 10))}>
          <SelectTrigger className="w-[130px] h-9" data-testid="hours-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {MONTH_LABELS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="outline" onClick={() => setOvertimeOpen(true)} data-testid="overtime-config-btn">
          <CalendarRange className="h-4 w-4 mr-1" />Calcul heures sup
        </Button>
        <Button variant="outline" onClick={() => setScheduleOpen(true)} data-testid="edit-schedule-btn">
          <Settings2 className="h-4 w-4 mr-1" />Horaires contractuels
        </Button>
        <Button onClick={() => setBatchOpen(true)} data-testid="add-hours-btn">
          <Plus className="h-4 w-4 mr-1" />Saisir des heures
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <>
          {/* KPI counters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-indigo-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-indigo-100 rounded-sm flex items-center justify-center">
                    <CalendarDays className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">{MONTH_LABELS[selectedMonth - 1]} {year} — effectif / théorique</p>
                    <p className="text-xl font-bold">
                      <span className="font-mono">{(monthly?.actual_hours || 0).toFixed(1)}h</span>
                      <span className="text-muted-foreground font-normal"> / {(monthly?.theoretical_hours || 0).toFixed(1)}h</span>
                    </p>
                    {monthly?.absence_hours > 0 && (
                      <p className="text-[11px] text-muted-foreground">dont {monthly.absence_hours.toFixed(1)}h d'absence déduites</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={monthDelta >= 0 ? "border-emerald-300" : "border-red-300"} data-testid="kpi-month-delta">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${monthDelta >= 0 ? "bg-emerald-100" : "bg-red-100"}`}>
                    {monthDelta >= 0
                      ? <TrendingUp className="h-5 w-5 text-emerald-600" />
                      : <TrendingDown className="h-5 w-5 text-red-600" />}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{monthDelta >= 0 ? "Heures supplémentaires" : "Déficit"} (mois)</p>
                    <p className={`text-2xl font-bold font-mono ${monthDelta >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                      {monthDelta >= 0 ? "+" : ""}{monthDelta.toFixed(1)}h
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={counterValue >= 0 ? "border-emerald-400 bg-emerald-50/30" : "border-red-400 bg-red-50/30"} data-testid="kpi-year-delta">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${counterValue >= 0 ? "bg-emerald-200" : "bg-red-200"}`}>
                    {counterValue >= 0
                      ? <TrendingUp className="h-5 w-5 text-emerald-700" />
                      : <TrendingDown className="h-5 w-5 text-red-700" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground truncate">{counterLabel}</p>
                    <p className={`text-2xl font-bold font-mono ${counterValue >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                      {counterValue >= 0 ? "+" : ""}{counterValue.toFixed(1)}h
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {isAnnual
                        ? `${(fiscalPeriod?.actual_hours || 0).toFixed(0)}h / ${(fiscalPeriod?.theoretical_hours || 0).toFixed(0)}h`
                        : `${(yearly?.actual_hours || 0).toFixed(0)}h / ${(yearly?.theoretical_hours || 0).toFixed(0)}h`}
                      {isAnnual && <Badge variant="outline" className="ml-2 text-[9px] border-indigo-300 text-indigo-700">Annuel</Badge>}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly breakdown table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Répartition mensuelle {year}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mois</TableHead>
                    <TableHead className="text-right">Théorique</TableHead>
                    <TableHead className="text-right">Absences</TableHead>
                    <TableHead className="text-right">Effectué</TableHead>
                    <TableHead className="text-right">Delta</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byMonth.map((bm) => {
                    const isCurrent = bm.month === selectedMonth;
                    return (
                      <TableRow
                        key={bm.month}
                        className={`cursor-pointer hover:bg-slate-50 ${isCurrent ? "bg-indigo-50/60" : ""}`}
                        onClick={() => setSelectedMonth(bm.month)}
                        data-testid={`hours-month-row-${bm.month}`}
                      >
                        <TableCell className="font-medium">{MONTH_LABELS[bm.month - 1]}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{bm.theoretical_hours.toFixed(1)}h</TableCell>
                        <TableCell className="text-right font-mono text-xs text-muted-foreground">
                          {bm.absence_hours > 0 ? `-${bm.absence_hours.toFixed(1)}h` : "—"}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">{bm.actual_hours.toFixed(1)}h</TableCell>
                        <TableCell className="text-right">
                          {bm.theoretical_hours === 0 && bm.actual_hours === 0 ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            <Badge
                              variant="outline"
                              className={`font-mono ${bm.delta_hours >= 0 ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-red-300 text-red-700 bg-red-50"}`}
                            >
                              {bm.delta_hours >= 0 ? "+" : ""}{bm.delta_hours.toFixed(1)}h
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Détail des heures pointées du mois sélectionné */}
          <HoursMonthDetail employee={employee} year={year} month={selectedMonth} />
        </>
      )}

      <HoursScheduleDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        employee={employee}
        initialSchedule={schedule}
        onSaved={() => { fetchData(); onRefresh?.(); }}
      />
      <HoursBatchEntryDialog
        open={batchOpen}
        onOpenChange={setBatchOpen}
        employee={employee}
        onSaved={() => { fetchData(); onRefresh?.(); }}
      />
      <OvertimeConfigDialog
        open={overtimeOpen}
        onOpenChange={setOvertimeOpen}
        onSaved={fetchData}
      />
    </div>
  );
};

export default HoursTab;
